'use client';

import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import {
  TrendingUp,
  Search,
  Users,
  BarChart3,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  ExternalLink,
  ChevronDown,
  Brain,
  Globe,
  Zap,
  ArrowRight } from
'lucide-react';
import { generateProspectId, type ProspectSubmission } from '@/lib/prospectStorage';
import { trackEvent } from '@/lib/analytics';

// ── Types ──────────────────────────────────────────────────────────────────
interface FormData {
  companyName: string;
  industry: string;
  individualName: string;
  title: string;
  phone: string;
  email: string;
  city: string;
  state: string;
}

const EMPTY_FORM: FormData = {
  companyName: '',
  industry: '',
  individualName: '',
  title: '',
  phone: '',
  email: '',
  city: '',
  state: ''
};

const US_STATES = [
'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA',
'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT',
'VA', 'WA', 'WV', 'WI', 'WY', 'DC'];


const PLATFORMS = ['ChatGPT', 'Gemini', 'Claude'] as const;

type Step = 'form' | 'running' | 'complete' | 'error';

// ── Stat counter hook ──────────────────────────────────────────────────────
function useCountUp(target: number, duration = 1800, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

// ── Score bar ──────────────────────────────────────────────────────────────
function ScoreBar({ platform, score }: {platform: string;score: number;}) {
  const barColor = score >= 60 ? 'bg-emerald-500' : score >= 35 ? 'bg-amber-500' : 'bg-red-500';
  const textColor = score >= 60 ? 'text-emerald-700' : score >= 35 ? 'text-amber-700' : 'text-red-700';
  const bgColor = score >= 60 ? 'bg-emerald-50' : score >= 35 ? 'bg-amber-50' : 'bg-red-50';
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-foreground">{platform}</span>
        <span className={`text-sm font-bold ${textColor} ${bgColor} px-2.5 py-0.5 rounded-full`}>
          {score}%
        </span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${barColor} transition-all duration-1000`} style={{ width: `${score}%` }} />
      </div>
    </div>);

}

// ── Main component ─────────────────────────────────────────────────────────
export default function LandingContent() {
  const formRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const [statsVisible, setStatsVisible] = useState(false);

  // Form state
  const [form, setForm] = useState<FormData>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [step, setStep] = useState<Step>('form');
  const [result, setResult] = useState<ProspectSubmission | null>(null);
  const [apiError, setApiError] = useState('');
  const [runningPlatform, setRunningPlatform] = useState(0);

  // Animated counters
  const c1 = useCountUp(58, 1600, statsVisible);
  const c2 = useCountUp(73, 1800, statsVisible);
  const c3 = useCountUp(3, 1200, statsVisible);

  // Intersection observer for stats
  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) setStatsVisible(true);},
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const scrollToForm = () => {
    trackEvent('cta_click', {
      event_category: 'conversion',
      event_label: 'Get My Free AI Visibility Report',
      page_location: typeof window !== 'undefined' ? window.location.href : '',
    });
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const validate = (): boolean => {
    const newErrors: Partial<FormData> = {};
    if (!form.companyName.trim()) newErrors.companyName = 'Required';
    if (!form.industry.trim()) newErrors.industry = 'Required';
    if (!form.individualName.trim()) newErrors.individualName = 'Required';
    if (!form.title.trim()) newErrors.title = 'Required';
    if (!form.phone.trim()) newErrors.phone = 'Required';
    if (!form.email.trim()) newErrors.email = 'Required';else
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Valid email required';
    if (!form.city.trim()) newErrors.city = 'Required';
    if (!form.state) newErrors.state = 'Required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const id = generateProspectId();
    setStep('running');
    setRunningPlatform(0);
    setApiError('');

    const interval = setInterval(() => {
      setRunningPlatform((prev) => {
        if (prev >= PLATFORMS.length - 1) {clearInterval(interval);return prev;}
        return prev + 1;
      });
    }, 1800);

    try {
      const response = await fetch('/api/prospect/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, ...form })
      });
      clearInterval(interval);
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Submission failed');
      }
      const data = await response.json();
      const submission: ProspectSubmission = {
        id,
        ...form,
        aiVisibilityScore: data.overallScore,
        platformScores: data.scores,
        competitors: data.competitors ?? {},
        reportLink: data.reportLink,
        submittedAt: new Date().toISOString(),
        reportStatus: 'complete'
      };
      setResult(submission);
      setStep('complete');
    } catch (err) {
      clearInterval(interval);
      setApiError(err instanceof Error ? err.message : 'An error occurred');
      setStep('error');
    }
  };

  // ── Running state ──────────────────────────────────────────────────────
  const RunningView = () =>
  <div className="py-16 px-6 flex flex-col items-center text-center space-y-8">
      <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center">
        <BarChart3 size={28} className="text-primary animate-pulse" />
      </div>
      <div>
        <h3 className="text-2xl font-bold text-foreground mb-2">Scanning AI Platforms</h3>
        <p className="text-muted-foreground text-sm">
          Checking visibility for <strong>{form.companyName}</strong> across all AI platforms…
        </p>
      </div>
      <div className="space-y-3 text-left w-full max-w-xs">
        {PLATFORMS.map((platform, i) =>
      <div key={platform} className="flex items-center gap-3">
            <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
        i < runningPlatform ? 'bg-emerald-100' : i === runningPlatform ? 'bg-primary/10' : 'bg-slate-100'}`
        }>
              {i < runningPlatform ?
          <CheckCircle2 size={16} className="text-emerald-600" /> :
          i === runningPlatform ?
          <Loader2 size={16} className="text-primary animate-spin" /> :

          <span className="h-2 w-2 rounded-full bg-slate-300" />
          }
            </div>
            <span className={`text-sm font-semibold transition-colors duration-300 ${
        i < runningPlatform ? 'text-emerald-700' : i === runningPlatform ? 'text-foreground' : 'text-muted-foreground'}`
        }>{platform}</span>
            {i < runningPlatform && <span className="ml-auto text-xs text-emerald-600 font-semibold">Done</span>}
          </div>
      )}
      </div>
    </div>;


  // ── Complete state ─────────────────────────────────────────────────────
  const CompleteView = () =>
  <div className="py-12 px-6 flex flex-col items-center text-center space-y-6">
      <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center">
        <CheckCircle2 size={32} className="text-emerald-600" />
      </div>
      <div>
        <h3 className="text-2xl font-bold text-foreground mb-2">Your Report Is Ready</h3>
        <p className="text-muted-foreground text-sm max-w-sm">
          We've scanned <strong>{result?.companyName}</strong> across ChatGPT, Gemini, and Claude. Your full AI Visibility Report has been sent to <strong>{result?.email}</strong>.
        </p>
      </div>
      {result?.platformScores &&
    <div className="w-full max-w-sm space-y-3 text-left">
          {Object.entries(result.platformScores).map(([platform, score]) =>
      <ScoreBar key={platform} platform={platform} score={score as number} />
      )}
        </div>
    }
      {result?.aiVisibilityScore !== undefined &&
    <div className="rounded-xl bg-primary/5 border border-primary/20 px-6 py-4 w-full max-w-sm">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Overall AI Visibility Score</p>
          <p className="text-4xl font-extrabold text-primary">{result.aiVisibilityScore}<span className="text-xl text-muted-foreground">/100</span></p>
        </div>
    }
      {result?.reportLink &&
    <a
      href={result.reportLink}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 bg-primary text-white font-semibold px-6 py-3 rounded-xl hover:bg-primary/90 transition-colors">
      
          View Full Report <ExternalLink size={16} />
        </a>
    }
    </div>;


  // ── Error state ────────────────────────────────────────────────────────
  const ErrorView = () =>
  <div className="py-12 px-6 flex flex-col items-center text-center space-y-4">
      <div className="h-14 w-14 rounded-full bg-red-100 flex items-center justify-center">
        <AlertTriangle size={26} className="text-red-600" />
      </div>
      <h3 className="text-xl font-bold text-foreground">Something went wrong</h3>
      <p className="text-sm text-muted-foreground max-w-sm">{apiError || 'We couldn\'t complete the scan. Please try again.'}</p>
      <button
      onClick={() => {setStep('form');setApiError('');}}
      className="mt-2 text-sm font-semibold text-primary hover:underline">
      
        Try again
      </button>
    </div>;


  return (
    <div className="min-h-screen bg-background font-sans">

      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-[#0a1628] text-white">
        {/* Animated gradient orbs */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-5%] w-[500px] h-[500px] rounded-full bg-[#1a56db]/20 blur-[120px]" />
          <div className="absolute bottom-[-10%] right-[-5%] w-[400px] h-[400px] rounded-full bg-[#7c3aed]/20 blur-[100px]" />
          <div className="absolute top-[40%] left-[50%] w-[300px] h-[300px] rounded-full bg-[#1a56db]/10 blur-[80px]" />
        </div>

        <div className="relative max-w-5xl mx-auto px-6 py-20 md:py-28">
          {/* Logo */}
          <div className="flex justify-center mb-10">
            <Image
              src="/assets/images/TRMG_StackedLogo_Small-1783198752219.png"
              alt="The Redwine Marketing Group stacked logo"
              width={120}
              height={80}
              className="object-contain brightness-0 invert opacity-90" />
            
          </div>

          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-xs font-semibold text-white/80 mb-6 backdrop-blur-sm">
              <Brain size={13} className="text-[#7c3aed]" />
              The AI Search Revolution Is Already Here
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight mb-6">
              Your Customers Are Asking AI.<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#60a5fa] to-[#a78bfa]">
                Is Your Business Answering?
              </span>
            </h1>
            <p className="text-lg text-white/70 leading-relaxed mb-10 max-w-2xl mx-auto">
              Search has fundamentally changed. ChatGPT, Gemini, and Claude now answer millions of buying questions every day — and most local businesses have no idea whether they're showing up or being left out entirely.
            </p>
            <button
              onClick={scrollToForm}
              className="inline-flex items-center gap-2.5 bg-[#1a56db] hover:bg-[#1a56db]/90 text-white font-bold px-8 py-4 rounded-xl text-base transition-all duration-200 shadow-lg shadow-[#1a56db]/30 hover:shadow-xl hover:shadow-[#1a56db]/40 hover:-translate-y-0.5">
              
              Get My Free AI Visibility Report
              <ArrowRight size={18} />
            </button>
            <p className="mt-3 text-xs text-white/40">No cost. No obligation. Results delivered automatically.</p>
          </div>

          <div className="flex justify-center mt-14">
            <button onClick={scrollToForm} className="text-white/30 hover:text-white/60 transition-colors animate-bounce">
              <ChevronDown size={24} />
            </button>
          </div>
        </div>
      </section>

      {/* ── AI Search Stats ────────────────────────────────────────────── */}
      <section ref={statsRef} className="bg-white border-b border-border py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs font-bold tracking-widest uppercase text-primary mb-3">By The Numbers</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-foreground mb-4">
              AI Search Is Growing Faster Than Anyone Predicted
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              This isn't a future trend — it's happening right now. Here's what the data says about how people are using AI to find businesses like yours.
            </p>
          </div>

          {/* Bento stats grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 mb-14">
            <div className="col-span-2 md:col-span-1 bg-gradient-to-br from-[#0a1628] to-[#1a2a4a] rounded-2xl p-7 text-white">
              <TrendingUp size={22} className="text-[#60a5fa] mb-4" />
              <div className="text-5xl font-extrabold mb-1">{c1}%</div>
              <p className="text-white/60 text-sm leading-snug">of consumers now use AI assistants to research local businesses before making a purchase decision</p>
            </div>
            <div className="bg-[#f0f7ff] rounded-2xl p-7">
              <Search size={22} className="text-primary mb-4" />
              <div className="text-5xl font-extrabold text-foreground mb-1">{c2}%</div>
              <p className="text-muted-foreground text-sm leading-snug">of AI-generated business recommendations go to the top 3 results — with zero clicks to a website</p>
            </div>
            <div className="bg-[#faf5ff] rounded-2xl p-7">
              <Globe size={22} className="text-accent mb-4" />
              <div className="text-5xl font-extrabold text-foreground mb-1">{c3}B+</div>
              <p className="text-muted-foreground text-sm leading-snug">AI search queries are processed every month across ChatGPT, Gemini, and Claude combined</p>
            </div>
          </div>

          {/* Platform comparison */}
          <div className="grid md:grid-cols-3 gap-4">
            {[
            { name: 'ChatGPT', color: '#10a37f', bg: '#f0fdf9', share: '45%', detail: 'Dominant in conversational search. Users ask "who is the best [service] near me?" and expect a direct answer.' },
            { name: 'Google Gemini', color: '#4285f4', bg: '#eff6ff', share: '31%', detail: 'Deeply integrated with Google Search. Businesses with strong local SEO have a head start — but it\'s not enough alone.' },
            { name: 'Claude', color: '#d97706', bg: '#fffbeb', share: '24%', detail: 'Preferred by professionals and B2B buyers. If your clients are businesses, Claude visibility matters more than you think.' }].
            map((p) =>
            <div key={p.name} className="rounded-xl border border-border p-5" style={{ backgroundColor: p.bg }}>
                <div className="flex items-center justify-between mb-3">
                  <span className="font-bold text-foreground">{p.name}</span>
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: p.color }}>{p.share} share</span>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.detail}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Why It Matters ────────────────────────────────────────────── */}
      <section className="bg-background py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-bold tracking-widest uppercase text-primary mb-3">The Shift Nobody Warned You About</p>
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground mb-5 leading-tight">
                Traditional SEO Isn't Enough Anymore
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-5">
                For two decades, ranking on Google's first page was the goal. But AI search doesn't show a list of links — it gives a direct answer. If your business isn't part of that answer, you're invisible to a growing segment of buyers.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-7">
                The businesses that win in AI search aren't necessarily the biggest or the oldest. They're the ones whose digital presence is structured in a way that AI systems can understand, trust, and recommend.
              </p>
              <div className="space-y-3">
                {[
                'AI answers don\'t include "sponsored" results — organic visibility is everything',
                'A single AI recommendation can replace dozens of Google clicks',
                'Your competitors may already be optimizing for AI search'].
                map((point) =>
                <div key={point} className="flex items-start gap-3">
                    <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                      <CheckCircle2 size={12} className="text-primary" />
                    </div>
                    <p className="text-sm text-foreground">{point}</p>
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-4">
              {[
              { label: 'Traditional Google Search', score: 62, note: 'Still important, but declining as primary discovery channel' },
              { label: 'AI Search Visibility', score: 18, note: 'Where most local businesses stand today — and where the opportunity is' },
              { label: 'Social Media Discovery', score: 44, note: 'Effective for brand awareness, less so for purchase intent' }].
              map((item) =>
              <div key={item.label} className="bg-card border border-border rounded-xl p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-foreground">{item.label}</span>
                    <span className="text-sm font-bold text-foreground">{item.score}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-2">
                    <div
                    className="h-full rounded-full bg-primary transition-all duration-1000"
                    style={{ width: `${item.score}%` }} />
                  
                  </div>
                  <p className="text-xs text-muted-foreground">{item.note}</p>
                </div>
              )}
              <p className="text-xs text-muted-foreground text-center">Average visibility scores for local service businesses, 2025</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── About Tim / TRMG ──────────────────────────────────────────── */}
      <section className="bg-[#0a1628] text-white py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-12">
            <p className="text-xs font-bold tracking-widest uppercase text-[#60a5fa] mb-3">About Tim & TRMG</p>
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4 leading-tight">
              Built in Central Indiana.<br />Built for What's Next.
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto leading-relaxed">
              The Redwine Marketing Group helps local businesses in central Indiana get found, get heard, and get hired in the new world of AI search.
            </p>
          </div>

          {/* Tim & Lisa bios */}
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {/* Tim */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-7 flex gap-5 items-start">
              <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0 bg-white/10 border border-white/20">
                <img
                  src="https://www.theredwinemg.com/images/a19669edde716e92a6df3a97a9748de62dcf7a38.webp"
                  alt="Tim Surber, Co-Founder of The Redwine Marketing Group"
                  className="w-full h-full object-cover" />
                
              </div>
              <div>
                <h3 className="font-bold text-white text-lg mb-0.5">Tim Surber</h3>
                <p className="text-[#60a5fa] text-xs font-semibold mb-3">Co-Founder</p>
                <p className="text-white/65 text-sm leading-relaxed">
                  Thirty-five years in executive sales leadership. Twenty-five years owning businesses across central Indiana — telecommunications, IT, low-voltage, and media. Built TRMG to help local businesses get found in the new world of AI search.
                </p>
              </div>
            </div>
            {/* Lisa */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-7 flex gap-5 items-start">
              <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0 bg-white/10 border border-white/20">
                <img
                  src="https://www.theredwinemg.com/images/6fa022c2a83122d53512707338fe847a0d2cef3e.webp"
                  alt="Lisa Surber, Co-Founder of The Redwine Marketing Group"
                  className="w-full h-full object-cover" />
                
              </div>
              <div>
                <h3 className="font-bold text-white text-lg mb-0.5">Lisa Surber</h3>
                <p className="text-[#60a5fa] text-xs font-semibold mb-3">Co-Founder</p>
                <p className="text-white/65 text-sm leading-relaxed">
                  The relationship side of TRMG. Lisa runs client connections and is the reason our members feel like they're working with a partner, not a vendor.
                </p>
              </div>
            </div>
          </div>

          {/* Founding story */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 mb-8">
            <p className="text-xs font-bold tracking-widest uppercase text-[#a78bfa] mb-4">Our Story</p>
            <div className="grid md:grid-cols-3 gap-6 text-white/65 text-sm leading-relaxed">
              <div>
                <h4 className="text-white font-semibold mb-2">Where We Started</h4>
                <p>
                  Tim and Lisa have lived in central Indiana for over 30 years. In 2009 they founded The Redwine Corporation — named after Lisa's maiden name — as the parent company for everything they'd build together. The name is a reminder that every business they've built has been a true partnership.
                </p>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-2">What We Built</h4>
                <p>
                  For sixteen years, The Redwine Corporation held ventures across central Indiana. In 2019 they launched Martinsville Neighbors, a community print publication, followed by Neighbors of Bargersville & Center Grove in 2023. Those years taught them how marketing actually works for small businesses.
                </p>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-2">Where We Are Now</h4>
                <p>
                  In 2025 they launched The Redwine Marketing Group. AI search was beginning to change how customers find local businesses — most local agencies didn't see it coming. TRMG was built for what comes next. We don't sell tools we haven't used ourselves.
                </p>
              </div>
            </div>
          </div>

          {/* Values */}
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[
            { label: 'Drive', desc: 'We initiate growth instead of waiting for it. New markets, new tools, new ways to help local businesses get found — we move first.' },
            { label: 'Determination', desc: 'We exceed what we agree to deliver. Every commitment comes with measurement, accountability, and follow-through.' },
            { label: 'Honest Communications', desc: 'We tell clients the truth, even when it costs us a sale. If we can\'t help your business, we\'ll say so and point you to who can.' },
            { label: 'Trust', desc: 'We earn it the hard way and we don\'t risk it for short-term wins. Long-term relationships beat quick transactions, every time.' }].
            map((v) =>
            <div key={v.label} className="bg-white/5 border border-white/10 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-2 w-2 rounded-full bg-[#a78bfa]" />
                  <span className="text-white font-bold text-sm">{v.label}</span>
                </div>
                <p className="text-white/55 text-xs leading-relaxed">{v.desc}</p>
              </div>
            )}
          </div>

          {/* Local roots callout */}
          <div className="bg-gradient-to-r from-[#1a2a4a] to-[#1a1a3a] border border-white/10 rounded-2xl p-7 flex flex-col md:flex-row items-center gap-6">
            <div className="w-20 h-20 rounded-xl overflow-hidden shrink-0 border border-white/20">
              <img
                src="https://www.theredwinemg.com/images/2a666bb9f827bb729c1cd2c7970ef7702c39bd26.webp"
                alt="Tim and Lisa Surber with their family in central Indiana"
                className="w-full h-full object-cover" />
              
            </div>
            <div>
              <h4 className="text-white font-bold mb-1">Built In Morgan County</h4>
              <p className="text-white/60 text-sm leading-relaxed">
                Lisa and I, along with our 5 children, have lived here for over 30 years. TRMG operates from Paragon and serves Morgan, Johnson, Monroe, Owen, Putnam, and Hendricks Counties. We're not an out-of-state agency cosplaying as local — we're your actual neighbors.
              </p>
            </div>
            <a
              href="https://calendar.app.google/65GNQNuTX5BYwhRn8"
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 inline-flex items-center gap-2 bg-[#1a56db] hover:bg-[#1a56db]/90 text-white font-semibold px-5 py-2.5 rounded-xl text-sm transition-colors whitespace-nowrap">
              
              Schedule a Free Call <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* ── How It Works ──────────────────────────────────────────────── */}
      <section className="bg-white border-y border-border py-14 md:py-18">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-xs font-bold tracking-widest uppercase text-primary mb-3">What Happens Next</p>
            <h2 className="text-3xl font-extrabold text-foreground">Fully Automated. Completely Free.</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
            { step: '01', icon: Users, title: 'Fill Out the Form', desc: 'Tell us your business name, location, and industry. Takes under 2 minutes.' },
            { step: '02', icon: Brain, title: 'AI Scans Run', desc: 'Our system queries ChatGPT, Gemini, and Claude to see how they describe and recommend your business.' },
            { step: '03', icon: BarChart3, title: 'Score Generated', desc: 'You receive a visibility score for each platform plus an overall AI Visibility Score out of 100.' },
            { step: '04', icon: Zap, title: 'Report Delivered', desc: 'Your full report is emailed to you automatically — no waiting, no sales call required.' }].
            map((item) => {
              const ItemIcon = item.icon;
              return (
                <div key={item.step} className="relative">
                  <div className="text-xs font-extrabold text-primary/30 mb-3 tracking-widest">{item.step}</div>
                  <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center mb-3">
                    <ItemIcon size={18} className="text-primary" />
                  </div>
                  <h3 className="font-bold text-foreground mb-1.5">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>);

            })}
          </div>
        </div>
      </section>

      {/* ── Prospect Form ─────────────────────────────────────────────── */}
      <section ref={formRef} className="bg-background py-16 md:py-20" id="get-report">
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-xs font-bold tracking-widest uppercase text-primary mb-3">Free AI Visibility Report</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-foreground mb-3">
              Find Out Where You Stand
            </h2>
            <p className="text-muted-foreground">
              Enter your business details below. Your report will be generated and emailed to you automatically — no cost, no commitment.
            </p>
          </div>

          <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
            {step === 'running' && <RunningView />}
            {step === 'complete' && <CompleteView />}
            {step === 'error' && <ErrorView />}
            {step === 'form' &&
            <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-5">
                {/* Row 1 */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Company Name *</label>
                    <input
                    type="text"
                    value={form.companyName}
                    onChange={(e) => handleChange('companyName', e.target.value)}
                    placeholder="Acme Plumbing Co."
                    className={`w-full rounded-lg border px-3.5 py-2.5 text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-shadow ${errors.companyName ? 'border-red-400' : 'border-input'}`} />
                  
                    {errors.companyName && <p className="mt-1 text-xs text-red-500">{errors.companyName}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Industry *</label>
                    <input
                    type="text"
                    value={form.industry}
                    onChange={(e) => handleChange('industry', e.target.value)}
                    placeholder="Plumbing, HVAC, Roofing…"
                    className={`w-full rounded-lg border px-3.5 py-2.5 text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-shadow ${errors.industry ? 'border-red-400' : 'border-input'}`} />
                  
                    {errors.industry && <p className="mt-1 text-xs text-red-500">{errors.industry}</p>}
                  </div>
                </div>

                {/* Row 2 */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Your Name *</label>
                    <input
                    type="text"
                    value={form.individualName}
                    onChange={(e) => handleChange('individualName', e.target.value)}
                    placeholder="Jane Smith"
                    className={`w-full rounded-lg border px-3.5 py-2.5 text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-shadow ${errors.individualName ? 'border-red-400' : 'border-input'}`} />
                  
                    {errors.individualName && <p className="mt-1 text-xs text-red-500">{errors.individualName}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Title *</label>
                    <input
                    type="text"
                    value={form.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    placeholder="Owner, GM, Marketing Director…"
                    className={`w-full rounded-lg border px-3.5 py-2.5 text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-shadow ${errors.title ? 'border-red-400' : 'border-input'}`} />
                  
                    {errors.title && <p className="mt-1 text-xs text-red-500">{errors.title}</p>}
                  </div>
                </div>

                {/* Row 3 */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Phone *</label>
                    <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="(555) 000-0000"
                    className={`w-full rounded-lg border px-3.5 py-2.5 text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-shadow ${errors.phone ? 'border-red-400' : 'border-input'}`} />
                  
                    {errors.phone && <p className="mt-1 text-xs text-red-500">{errors.phone}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Email *</label>
                    <input
                    type="email"
                    value={form.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="jane@acmeplumbing.com"
                    className={`w-full rounded-lg border px-3.5 py-2.5 text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-shadow ${errors.email ? 'border-red-400' : 'border-input'}`} />
                  
                    {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
                  </div>
                </div>

                {/* Row 4 */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">City *</label>
                    <input
                    type="text"
                    value={form.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    placeholder="Indianapolis"
                    className={`w-full rounded-lg border px-3.5 py-2.5 text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-shadow ${errors.city ? 'border-red-400' : 'border-input'}`} />
                  
                    {errors.city && <p className="mt-1 text-xs text-red-500">{errors.city}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">State *</label>
                    <select
                    value={form.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                    className={`w-full rounded-lg border px-3.5 py-2.5 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-shadow ${errors.state ? 'border-red-400' : 'border-input'}`}>
                    
                      <option value="">Select state…</option>
                      {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {errors.state && <p className="mt-1 text-xs text-red-500">{errors.state}</p>}
                  </div>
                </div>

                <button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-3.5 rounded-xl text-sm transition-all duration-200 flex items-center justify-center gap-2 shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/30 hover:-translate-y-0.5">
                
                  <BarChart3 size={16} />
                  Generate My Free AI Visibility Report
                </button>
                <p className="text-center text-xs text-muted-foreground">
                  Your information is used only to run your AI visibility scan. We don't sell or share your data.
                </p>
              </form>
            }
          </div>
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────────────── */}
      <footer className="bg-[#0a1628] text-white/50 py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-2">
            <Image
              src="/assets/images/TRMG_StackedLogo_Small-1783198752219.png"
              alt="The Redwine Marketing Group"
              width={60}
              height={40}
              className="object-contain brightness-0 invert opacity-40" />
            
            <span>© {new Date().getFullYear()} The Redwine Marketing Group. All rights reserved.</span>
          </div>
          <span>AI Visibility Report — Powered by TRMG</span>
        </div>
      </footer>
    </div>);

}